/**
 * PageHero Component
 * 
 * Reusable hero section for internal pages.
 * Features a brand orange gradient background with centered title.
 * 
 * Props:
 * - title: The main heading text
 * - subtitle: Optional subtitle text
 * - size: 'sm' | 'md' | 'lg' - controls hero height
 */

import { motion } from 'framer-motion'

const sizes = {
  sm: 'py-24 pt-32',
  md: 'py-32 pt-40',
  lg: 'py-40 pt-48',
}

const textures = {
  'warm-linen': {
    baseClass: 'bg-cream-300',
    baseStyle: {
      backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(109,42,20,0.07) 1px, transparent 0)',
      backgroundSize: '14px 14px',
    },
    overlayStyle: {
      backgroundImage: 'repeating-linear-gradient(90deg, rgba(47,40,34,0.06), rgba(47,40,34,0.06) 1px, transparent 1px, transparent 22px)',
      opacity: 1,
    }
  },
  'sage-noise': {
    baseClass: 'bg-[#d8dfd4]',
    baseStyle: {
      backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(0,82,0,0.08), transparent 70%)',
    },
    overlayStyle: {
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E\")",
      opacity: 0.14,
      mixBlendMode: 'multiply',
    }
  },
  'terracotta-sand': {
    baseClass: 'bg-[#e8d2c0]',
    baseStyle: {
      backgroundImage: 'radial-gradient(circle at 30% 20%, rgba(197,72,25,0.12), transparent 50%)',
    },
    overlayStyle: {
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E\")",
      opacity: 0.15,
      mixBlendMode: 'multiply',
    }
  },
  'earth-grain': {
    baseClass: 'bg-[#d8c4ac]',
    baseStyle: {
      backgroundImage: 'radial-gradient(circle at 22% 18%, rgba(197,72,25,0.18), transparent 35%), radial-gradient(circle at 80% 70%, rgba(0,82,0,0.12), transparent 45%)',
    },
    overlayStyle: {
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.82' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E\")",
      opacity: 0.12,
      mixBlendMode: 'multiply',
    }
  },
  // Dreamy motion-blur photograph — warm terracotta / ochre / cream streaks
  'orange-phase': {
    baseClass: 'bg-cream-200',
    baseStyle: {
      backgroundImage: "url('/optimized/orange-phase-wide-1376.webp')",
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    },
    overlayStyle: {
      backgroundImage: 'linear-gradient(to bottom, rgba(245,240,232,0) 0%, rgba(245,240,232,0.35) 100%)',
      opacity: 1,
    }
  },
  // Warm orange sunrise glow on cream-sage — grainy gradient
  'orange-hue': {
    baseClass: 'bg-cream-200',
    baseStyle: {
      backgroundImage: "url('/optimized/orange-hue-wide-1376.webp')",
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    },
    overlayStyle: {
      backgroundImage: 'linear-gradient(to right, rgba(245,240,232,0) 0%, rgba(245,240,232,0.25) 60%, rgba(245,240,232,0.55) 100%)',
      opacity: 1,
    }
  },
  // Sage-green pinstripe linen — tiles natively, no AI extension needed
  'green-linen': {
    baseClass: 'bg-[#94a876]',
    baseStyle: {
      backgroundImage: "url('/optimized/green-linen-800.webp')",
      backgroundSize: '400px auto',
      backgroundRepeat: 'repeat',
    },
    overlayStyle: {
      backgroundImage: 'radial-gradient(ellipse at center, rgba(245,240,232,0) 0%, rgba(245,240,232,0.15) 100%)',
      opacity: 1,
    }
  },
}

const PageHero = ({ title, subtitle, size = 'md', texture = 'warm-linen', children }) => {
  const activeTexture = textures[texture] || textures['warm-linen']

  return (
    <section className={`relative ${sizes[size]} ${activeTexture.baseClass} overflow-hidden rounded-b-[24px] md:rounded-b-[100px] z-10`}>
      {/* Textured background */}
      <div
        className="absolute inset-0"
        style={activeTexture.baseStyle}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0"
        style={activeTexture.overlayStyle}
        aria-hidden="true"
      />

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
        {subtitle && (
          <motion.p
            className="font-script text-dark-600 italic text-base md:text-lg mb-4"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
          >
            {subtitle}
          </motion.p>
        )}
        <motion.h1
          className="font-display text-dark-800 text-4xl md:text-5xl lg:text-6xl leading-[1.1] font-medium"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.3, ease: 'easeOut' }}
        >
          {title}
        </motion.h1>
        {children && (
          <motion.div
            className="mt-6"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease: 'easeOut' }}
          >
            {children}
          </motion.div>
        )}
      </div>
    </section>
  )
}

export default PageHero
